import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, ArrowRight, Check, ShoppingBag, FlaskConical, Shield, Leaf, Package, Minus, Plus, Trash2 } from 'lucide-react';
import { products } from '../data/products';
import { useSettings } from '../context/SettingsContext';
import { formatMoney, computeTotals } from '../utils/money';


// Shared page header with background image
const PageHeader = ({ eyebrow, title, subtitle, imageUrl, children }) => (
  <div style={{
    position: 'relative',
    overflow: 'hidden',
    minHeight: '260px',
    display: 'flex', alignItems: 'center',
  }}>
    <div style={{
      position: 'absolute', inset: 0,
      backgroundImage: `url(${imageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      filter: 'brightness(0.35) saturate(0.8)',
    }} />
    <div style={{
      position: 'absolute', inset: 0,
      background: 'linear-gradient(135deg, rgba(26,58,42,0.85) 0%, rgba(26,58,42,0.5) 100%)',
    }} />
    <div style={{
      position: 'relative', zIndex: 2,
      maxWidth: '1280px', margin: '0 auto',
      padding: '64px 32px',
      width: '100%',
    }}>
      {eyebrow && (
        <p style={{
          fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '12px',
        }}>{eyebrow}</p>
      )}
      <h1 style={{
        fontFamily: 'Syne', fontWeight: 700,
        fontSize: 'clamp(2rem, 4vw, 3rem)',
        color: 'var(--white)', letterSpacing: '-0.01em', marginBottom: subtitle ? '10px' : 0,
        lineHeight: '1.1',
      }}>{title}</h1>
      {subtitle && (
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem', marginTop: '8px' }}>
          {subtitle}
        </p>
      )}
      {children}
    </div>
  </div>
);

// ─── BUILD BOX MODAL ─────────────────────────────────────────────────────────
const BuildBoxModal = ({ selected, total, discounted, onClose, onAddToCart }) => {
  const handleConfirm = () => {
    // Add entire box as a single bundle item
    const boxItem = {
      id: `custom-box-${Date.now()}`,
      name: `Custom Box (${selected.length} items)`,
      mg: 'Bundle',
      category: 'bundles',
      price: parseFloat(discounted.toFixed(2)),
      originalPrice: parseFloat(total.toFixed(2)),
      image: selected[0]?.image || null,
      gradient: 'linear-gradient(135deg, #fff9e6 0%, #ffd54f 100%)',
      items: selected,
      isBundle: true,
    };
    onAddToCart && onAddToCart(boxItem);
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 30 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          onClick={e => e.stopPropagation()}
          style={{
            background: 'var(--white)',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '540px',
            overflow: 'hidden',
            boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
          }}
        >
          {/* Modal header */}
          <div style={{
            background: 'var(--forest)',
            padding: '24px 28px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                background: 'rgba(255,255,255,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Package size={20} color="var(--gold)" />
              </div>
              <div>
                <h3 style={{
                  fontFamily: 'Syne', fontWeight: 700, fontSize: '1.05rem',
                  color: 'var(--white)', marginBottom: '2px',
                }}>Your Custom Box</h3>
                <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                  {selected.length} item{selected.length !== 1 ? 's' : ''} · 15% bundle discount applied
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.1)', border: 'none',
                borderRadius: '8px', width: '32px', height: '32px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--white)',
              }}
            >
              <X size={16} />
            </button>
          </div>

          {/* Product list */}
          <div style={{ padding: '20px 28px', maxHeight: '280px', overflowY: 'auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {selected.map((p, i) => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 12px',
                  background: 'var(--smoke)', borderRadius: '12px',
                }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '8px',
                    overflow: 'hidden', flexShrink: 0, background: p.gradient,
                  }}>
                    {p.image && (
                      <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontFamily: 'Syne', fontWeight: 700, fontSize: '0.8rem',
                      color: 'var(--charcoal)', whiteSpace: 'nowrap',
                      overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{p.name}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--mid)' }}>{p.mg}</div>
                  </div>
                  <div style={{
                    fontFamily: 'Syne', fontWeight: 700, fontSize: '0.88rem',
                    color: 'var(--forest)', flexShrink: 0,
                  }}>${p.price.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing + CTA */}
          <div style={{
            padding: '0 28px 28px',
          }}>
            <div style={{
              background: 'var(--smoke)', borderRadius: '14px',
              padding: '16px 20px', marginBottom: '16px',
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: '0.82rem', color: 'var(--mid)', marginBottom: '8px',
              }}>
                <span>Original Total</span>
                <span style={{ textDecoration: 'line-through' }}>${total.toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: '0.82rem', color: '#4a7c59', fontWeight: 600,
                marginBottom: '10px',
              }}>
                <span>15% Bundle Discount</span>
                <span>−${(total * 0.15).toFixed(2)}</span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontFamily: 'Syne', fontWeight: 700, fontSize: '1.1rem',
                color: 'var(--charcoal)',
                paddingTop: '10px',
                borderTop: '1px solid var(--mist)',
              }}>
                <span>Total</span>
                <span style={{ color: 'var(--forest)' }}>${discounted.toFixed(2)}</span>
              </div>
            </div>

            <button
              onClick={handleConfirm}
              style={{
                width: '100%', padding: '15px',
                background: 'var(--forest)', color: 'var(--white)',
                border: 'none', borderRadius: '12px',
                fontFamily: 'Syne', fontWeight: 700, fontSize: '0.92rem',
                letterSpacing: '0.05em', textTransform: 'uppercase',
                cursor: 'pointer', transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-green)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--forest)'}
            >
              <ShoppingBag size={16} />
              Add Box to Cart
            </button>
            <p style={{
              textAlign: 'center', fontSize: '0.72rem', color: 'var(--mid)',
              marginTop: '10px',
            }}>This box will appear as a single item in your cart</p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── BUILD BOX PAGE ──────────────────────────────────────────────────────────
export const BuildBoxPage = ({ onAddToCart }) => {
  const [selected, setSelected] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const MAX = 6;
  const PER_PAGE = 9;

  const totalPages = Math.ceil(products.length / PER_PAGE);
  const pagedProducts = products.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const toggle = (product) => {
    if (selected.find(s => s.id === product.id)) {
      setSelected(selected.filter(s => s.id !== product.id));
    } else if (selected.length < MAX) {
      setSelected([...selected, product]);
    }
  };

  const total = selected.reduce((sum, p) => sum + p.price, 0);
  const discounted = total * 0.85;

  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh', background: 'var(--cream)' }}>
      <PageHeader
        eyebrow="Save 15%"
        title="Build Your Box"
        subtitle={`Choose any ${MAX} products and save 15% automatically`}
        imageUrl="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1400&q=80"
      />

      <div style={{
        maxWidth: '1280px', margin: '0 auto', padding: '24px 16px',
        display: 'grid', gridTemplateColumns: '1fr 320px', gap: '32px',
      }} className="build-grid">

        {/* Product picker */}
        <div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '20px', flexWrap: 'wrap', gap: '12px',
          }}>
            <h3 style={{
              fontFamily: 'Syne', fontWeight: 700, fontSize: '1.1rem',
              color: 'var(--charcoal)',
            }}>Select Products ({selected.length}/{MAX})</h3>
            <div style={{ display: 'flex', gap: '4px' }} className="build-progress-desktop">
              {[...Array(MAX)].map((_, i) => (
                <div key={i} style={{
                  width: '24px', height: '6px', borderRadius: '3px',
                  background: i < selected.length ? 'var(--gold)' : 'var(--mist)',
                  transition: 'background 0.2s',
                }} />
              ))}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '14px',
          }}>
            {pagedProducts.map((product) => {
              const isSelected = !!selected.find(s => s.id === product.id);
              const isDisabled = selected.length >= MAX && !isSelected;
              return (
                <motion.div
                  key={product.id}
                  whileHover={!isDisabled ? { y: -3 } : {}}
                  onClick={() => !isDisabled && toggle(product)}
                  style={{
                    background: 'var(--white)',
                    borderRadius: '14px',
                    border: isSelected ? '2px solid var(--forest)' : '1.5px solid var(--mist)',
                    overflow: 'hidden',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.45 : 1,
                    position: 'relative',
                    transition: 'all 0.2s',
                    boxShadow: isSelected ? '0 6px 24px rgba(26,58,42,0.12)' : 'none',
                  }}
                >
                  {isSelected && (
                    <div style={{
                      position: 'absolute', top: '8px', right: '8px', zIndex: 2,
                      width: '22px', height: '22px', borderRadius: '50%',
                      background: 'var(--forest)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Check size={12} color="white" />
                    </div>
                  )}
                  <div style={{
                    height: '110px', background: product.gradient, overflow: 'hidden',
                  }}>
                    {product.image ? (
                      <img src={product.image} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: product.gradient }} />
                    )}
                  </div>
                  <div style={{ padding: '12px 14px' }}>
                    <div style={{
                      fontFamily: 'Syne', fontWeight: 700, fontSize: '0.8rem',
                      color: 'var(--charcoal)', marginBottom: '6px', lineHeight: '1.3',
                    }}>{product.name}</div>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                      <span style={{ fontSize: '0.73rem', color: 'var(--mid)' }}>{product.category}</span>
                      <span style={{
                        fontFamily: 'Syne', fontWeight: 700, fontSize: '0.85rem',
                        color: 'var(--forest)',
                      }}>${product.price}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '8px', marginTop: '32px', flexWrap: 'wrap',
            }}>
              <button
                onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={page === 1}
                style={{
                  padding: '8px 16px', borderRadius: '8px',
                  background: page === 1 ? 'var(--smoke)' : 'var(--white)',
                  color: page === 1 ? 'var(--light-text)' : 'var(--charcoal)',
                  border: '1.5px solid var(--mist)',
                  fontFamily: 'Syne', fontWeight: 600, fontSize: '0.8rem',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
              >← Prev</button>

              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setPage(i + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  style={{
                    width: '38px', height: '38px', borderRadius: '8px',
                    background: page === i + 1 ? 'var(--forest)' : 'var(--white)',
                    color: page === i + 1 ? 'var(--white)' : 'var(--charcoal)',
                    border: page === i + 1 ? 'none' : '1.5px solid var(--mist)',
                    fontFamily: 'Syne', fontWeight: 700, fontSize: '0.85rem',
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >{i + 1}</button>
              ))}

              <button
                onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={page === totalPages}
                style={{
                  padding: '8px 16px', borderRadius: '8px',
                  background: page === totalPages ? 'var(--smoke)' : 'var(--white)',
                  color: page === totalPages ? 'var(--light-text)' : 'var(--charcoal)',
                  border: '1.5px solid var(--mist)',
                  fontFamily: 'Syne', fontWeight: 600, fontSize: '0.8rem',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                }}
              >Next →</button>
            </div>
          )}
        </div>

        {/* Desktop sidebar summary */}
        <div style={{ position: 'sticky', top: '88px', height: 'fit-content' }} className="build-sidebar">
          <div style={{
            background: 'var(--white)', borderRadius: '20px',
            border: '1px solid var(--mist)', overflow: 'hidden',
          }}>
            <div style={{ background: 'var(--forest)', padding: '22px 24px' }}>
              <h3 style={{
                fontFamily: 'Syne', fontWeight: 700, fontSize: '1.05rem',
                color: 'var(--white)', marginBottom: '4px',
              }}>Your Box</h3>
              <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>
                {selected.length}/{MAX} items selected
              </p>
            </div>
            <div style={{ padding: '20px' }}>
              {selected.length === 0 ? (
                <div style={{
                  textAlign: 'center', padding: '32px 0',
                  color: 'var(--mid)', fontSize: '0.85rem',
                }}>
                  <div style={{
                    width: '48px', height: '48px',
                    background: 'var(--smoke)', borderRadius: '12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 14px',
                  }}>
                    <ShoppingBag size={20} color="var(--mid)" />
                  </div>
                  Start adding products from the left
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                  {selected.map(p => (
                    <div key={p.id} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px', background: 'var(--smoke)', borderRadius: '10px',
                    }}>
                      <div style={{
                        width: '40px', height: '40px', borderRadius: '8px',
                        overflow: 'hidden', flexShrink: 0, background: p.gradient,
                      }}>
                        {p.image && (
                          <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: 'Syne', fontWeight: 700, fontSize: '0.78rem',
                          color: 'var(--charcoal)', whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{p.name}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--mid)' }}>${p.price}</div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); toggle(p); }} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--mid)', padding: '2px',
                        display: 'flex', alignItems: 'center',
                      }}><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}

              {selected.length > 0 && (
                <>
                  <div style={{
                    padding: '16px 0', borderTop: '1px solid var(--mist)',
                    borderBottom: '1px solid var(--mist)', marginBottom: '16px',
                  }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.82rem', color: 'var(--mid)', marginBottom: '8px',
                    }}>
                      <span>Original Total</span>
                      <span style={{ textDecoration: 'line-through' }}>${total.toFixed(2)}</span>
                    </div>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.82rem', color: 'var(--accent-green)', fontWeight: 600,
                      marginBottom: '8px',
                    }}>
                      <span>15% Bundle Discount</span>
                      <span>−${(total * 0.15).toFixed(2)}</span>
                    </div>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      fontFamily: 'Syne', fontWeight: 700, fontSize: '1rem',
                      color: 'var(--charcoal)',
                    }}>
                      <span>Total</span>
                      <span style={{ color: 'var(--forest)' }}>${discounted.toFixed(2)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowModal(true)}
                    style={{
                      width: '100%', padding: '15px',
                      background: 'var(--forest)', color: 'var(--white)',
                      border: 'none', borderRadius: '10px',
                      fontFamily: 'Syne', fontWeight: 700, fontSize: '0.88rem',
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                      cursor: 'pointer', transition: 'all 0.2s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-green)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--forest)'}
                  >
                    Review & Add to Cart <ArrowRight size={15} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile sticky bottom bar (always visible on mobile when 1+ items selected) ── */}
      <AnimatePresence>
        {selected.length > 0 && (
          <motion.div
            className="build-mobile-bar"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 26 }}
            style={{
              display: 'none', /* shown via CSS on mobile */
              position: 'fixed', bottom: 0, left: 0, right: 0,
              zIndex: 300,
              background: 'var(--white)',
              borderTop: '1px solid var(--mist)',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.12)',
              padding: '14px 18px',
            }}
          >
            {/* Progress bar */}
            <div style={{ display: 'flex', gap: '5px', marginBottom: '12px' }}>
              {[...Array(MAX)].map((_, i) => (
                <div key={i} style={{
                  flex: 1, height: '4px', borderRadius: '2px',
                  background: i < selected.length ? 'var(--gold)' : 'var(--mist)',
                  transition: 'background 0.25s',
                }} />
              ))}
            </div>

            {/* Summary row */}
            <div style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: '12px',
            }}>
              {/* Left: count + items preview */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                {/* Thumbnail stack */}
                <div style={{ display: 'flex', position: 'relative', flexShrink: 0 }}>
                  {selected.slice(0, 3).map((p, i) => (
                    <div key={p.id} style={{
                      width: '32px', height: '32px',
                      borderRadius: '8px', overflow: 'hidden',
                      border: '2px solid var(--white)',
                      background: p.gradient,
                      marginLeft: i === 0 ? 0 : '-10px',
                      zIndex: 3 - i,
                      position: 'relative',
                      flexShrink: 0,
                    }}>
                      {p.image && <img src={p.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </div>
                  ))}
                  {selected.length > 3 && (
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      background: 'var(--forest)', border: '2px solid var(--white)',
                      marginLeft: '-10px', zIndex: 0, position: 'relative',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.65rem', fontWeight: 700, color: 'var(--white)',
                      flexShrink: 0,
                    }}>+{selected.length - 3}</div>
                  )}
                </div>

                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'Syne', fontWeight: 700, fontSize: '0.85rem',
                    color: 'var(--charcoal)',
                  }}>{selected.length}/{MAX} items</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
                    <span style={{
                      fontFamily: 'Syne', fontWeight: 700, fontSize: '0.95rem',
                      color: 'var(--forest)',
                    }}>${discounted.toFixed(2)}</span>
                    <span style={{
                      fontSize: '0.72rem', color: 'var(--mid)',
                      textDecoration: 'line-through',
                    }}>${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Right: CTA button */}
              <button
                onClick={() => setShowModal(true)}
                style={{
                  flexShrink: 0,
                  background: 'var(--forest)', color: 'var(--white)',
                  border: 'none', borderRadius: '12px',
                  padding: '13px 20px',
                  fontFamily: 'Syne', fontWeight: 700, fontSize: '0.82rem',
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '7px',
                }}
              >
                <ShoppingBag size={15} />
                {selected.length === MAX ? 'Add to Cart' : 'Review Box'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal */}
      {showModal && (
        <BuildBoxModal
          selected={selected}
          total={total}
          discounted={discounted}
          onClose={() => setShowModal(false)}
          onAddToCart={(boxItem) => {
            onAddToCart && onAddToCart(boxItem);
            setSelected([]);
          }}
        />
      )}

      <style>{`
        @media (max-width: 900px) {
          .build-grid { grid-template-columns: 1fr !important; }
          .build-sidebar { display: none !important; }
          .build-mobile-bar { display: block !important; }
          .build-progress-desktop { display: none !important; }
        }
        @media (max-width: 900px) {
          /* Add bottom padding so last product card isn't hidden behind sticky bar */
          .build-grid { padding-bottom: 100px !important; }
        }
        @media (max-width: 480px) {
          .build-grid { padding: 16px 16px 100px !important; }
        }
      `}</style>
    </div>
  );
};

// ─── CART PAGE ───────────────────────────────────────────────────────────────
export const CartPage = ({ cartItems = [], onUpdateQty = () => {}, onRemove = () => {} }) => {
  const { settings } = useSettings();
  const { subtotal, shipping, tax, total, taxRate } = computeTotals(cartItems, settings);
  const money = (v) => formatMoney(v, settings);

  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh', background: 'var(--cream)' }}>
      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '48px 32px' }}>
        <h1 style={{
          fontFamily: 'Syne', fontWeight: 700, fontSize: '2.2rem',
          color: 'var(--forest)', letterSpacing: '-0.01em', marginBottom: '36px',
        }}>Your Cart</h1>

        {cartItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{
              width: '72px', height: '72px',
              background: 'var(--smoke)', borderRadius: '18px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <ShoppingBag size={30} color="var(--mid)" />
            </div>
            <h3 style={{
              fontFamily: 'Syne', fontWeight: 700, fontSize: '1.4rem',
              color: 'var(--charcoal)', marginBottom: '10px',
            }}>Your cart is empty</h3>
            <p style={{ color: 'var(--mid)', marginBottom: '28px', fontSize: '0.95rem' }}>
              Find something you'll love.
            </p>
            <Link to="/shop" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'var(--forest)', color: 'var(--white)',
              borderRadius: '8px', padding: '14px 28px',
              fontFamily: 'Syne', fontWeight: 700, fontSize: '0.88rem',
              letterSpacing: '0.05em', textTransform: 'uppercase',
              textDecoration: 'none',
            }}>Shop Now <ArrowRight size={14} /></Link>
          </div>
        ) : (
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 320px', gap: '40px',
          }} className="cart-grid">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {cartItems.map((item, i) => (
                <div key={i} style={{
                  background: 'var(--white)', borderRadius: '16px',
                  padding: '20px', border: '1px solid var(--mist)',
                  display: 'flex', gap: '16px', alignItems: 'flex-start',
                }}>
                  <div style={{
                    width: '72px', height: '72px',
                    borderRadius: '12px', overflow: 'hidden',
                    background: item.gradient, flexShrink: 0,
                  }}>
                    {item.image && (
                      <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontFamily: 'Syne', fontWeight: 700, fontSize: '0.95rem',
                      color: 'var(--charcoal)', marginBottom: '4px',
                    }}>{item.name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--mid)', marginBottom: '6px' }}>{item.mg}</div>

                    {/* If it's a bundle box, show the sub-items */}
                    {item.isBundle && item.items && (
                      <div style={{
                        marginTop: '8px',
                        background: 'var(--smoke)',
                        borderRadius: '8px',
                        padding: '10px 12px',
                      }}>
                        <div style={{
                          fontSize: '0.7rem', fontWeight: 700, color: 'var(--mid)',
                          textTransform: 'uppercase', letterSpacing: '0.08em',
                          marginBottom: '6px',
                        }}>Box Contents ({item.items.length} items)</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {item.items.map((sub, j) => (
                            <div key={j} style={{
                              display: 'flex', justifyContent: 'space-between',
                              fontSize: '0.75rem', color: 'var(--charcoal)',
                            }}>
                              <span>· {sub.name}</span>
                              <span style={{ color: 'var(--mid)' }}>{money(sub.price)}</span>
                            </div>
                          ))}
                        </div>
                        {item.originalPrice && (
                          <div style={{
                            marginTop: '8px', paddingTop: '8px',
                            borderTop: '1px solid var(--mist)',
                            display: 'flex', justifyContent: 'space-between',
                            fontSize: '0.73rem', color: '#4a7c59', fontWeight: 600,
                          }}>
                            <span>15% bundle discount</span>
                            <span>−{money(item.originalPrice - item.price)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                    gap: '10px', flexShrink: 0,
                  }}>
                    <div style={{
                      fontFamily: 'Syne', fontWeight: 700, fontSize: '1.05rem',
                      color: 'var(--forest)',
                    }}>
                      {money(item.price * (item.qty || 1))}
                      {item.originalPrice && (
                        <div style={{
                          fontSize: '0.72rem', color: 'var(--mid)',
                          textDecoration: 'line-through', fontWeight: 400, textAlign: 'right',
                        }}>{money(item.originalPrice * (item.qty || 1))}</div>
                      )}
                    </div>

                    {/* Quantity stepper */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      background: 'var(--smoke)', borderRadius: '8px', padding: '4px 8px',
                    }}>
                      <button
                        aria-label="Decrease quantity"
                        onClick={() => onUpdateQty(item.id, (item.qty || 1) - 1)}
                        style={{ background: 'none', display: 'flex', color: 'var(--charcoal)' }}
                      >
                        <Minus size={14} />
                      </button>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, minWidth: '16px', textAlign: 'center' }}>
                        {item.qty || 1}
                      </span>
                      <button
                        aria-label="Increase quantity"
                        onClick={() => onUpdateQty(item.id, (item.qty || 1) + 1)}
                        style={{ background: 'none', display: 'flex', color: 'var(--charcoal)' }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    <button
                      aria-label="Remove item"
                      onClick={() => onRemove(item.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        background: 'none', color: 'var(--mid)', fontSize: '0.72rem',
                      }}
                    >
                      <Trash2 size={13} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              background: 'var(--white)', borderRadius: '20px',
              border: '1px solid var(--mist)', padding: '28px',
              height: 'fit-content', position: 'sticky', top: '88px',
            }}>
              <h3 style={{
                fontFamily: 'Syne', fontWeight: 700, fontSize: '1.05rem',
                color: 'var(--charcoal)', marginBottom: '20px',
              }}>Order Summary</h3>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: '0.85rem', color: 'var(--mid)', marginBottom: '8px',
              }}>
                <span>Subtotal ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''})</span>
                <span>{money(subtotal)}</span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: '0.85rem', color: 'var(--accent-green)', fontWeight: 500, marginBottom: '8px',
              }}>
                <span>Shipping</span>
                <span>{shipping === 0 ? 'FREE' : money(shipping)}</span>
              </div>
              {tax > 0 && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: '0.85rem', color: 'var(--mid)', marginBottom: '8px',
                }}>
                  <span>Tax ({Math.round(taxRate * 100)}%)</span>
                  <span>{money(tax)}</span>
                </div>
              )}
              <div style={{
                borderTop: '1px solid var(--mist)', paddingTop: '16px', marginTop: '12px',
                marginBottom: '20px',
                display: 'flex', justifyContent: 'space-between',
                fontFamily: 'Syne', fontWeight: 700, fontSize: '1.1rem', color: 'var(--charcoal)',
              }}>
                <span>Total</span>
                <span style={{ color: 'var(--forest)' }}>{money(total)}</span>
              </div>
              <Link to="/checkout" style={{
                display: 'block', textAlign: 'center', width: '100%', padding: '15px',
                background: 'var(--gold)', color: 'var(--white)',
                border: 'none', borderRadius: '10px',
                fontFamily: 'Syne', fontWeight: 700, fontSize: '0.9rem',
                letterSpacing: '0.05em', textTransform: 'uppercase',
                cursor: 'pointer', marginBottom: '12px', textDecoration: 'none',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--gold-light)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--gold)'}>
                Proceed to Checkout
              </Link>
              <p style={{
                textAlign: 'center', fontSize: '0.72rem', color: 'var(--mid)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              }}>
                <Shield size={12} /> Age verified checkout · Adult signature required
              </p>
            </div>
          </div>
        )}
      </div>
      <style>{`
        @media (max-width: 760px) {
          .cart-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

// ─── LAB RESULTS PAGE ────────────────────────────────────────────────────────
export const LabResultsPage = () => {
  const [search, setSearch] = useState('');
  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh', background: 'var(--cream)' }}>
      <PageHeader
        eyebrow="Transparency First"
        title="Lab Results (COAs)"
        imageUrl="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=1400&q=80"
      >
        {/* Search bar — white bg so it's always visible */}
        <div style={{ position: 'relative', maxWidth: '480px', marginTop: '20px' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            style={{
              width: '100%', padding: '13px 48px 13px 20px',
              borderRadius: '8px',
              border: '1.5px solid rgba(255,255,255,0.35)',
              background: 'rgba(255,255,255,0.92)',
              color: '#1a3a2a', fontFamily: 'DM Sans', fontSize: '0.92rem',
              outline: 'none', boxSizing: 'border-box',
            }}
          />
          <svg style={{
            position: 'absolute', right: '16px', top: '50%',
            transform: 'translateY(-50%)', pointerEvents: 'none',
          }} width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="#4a7c59" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
        </div>
      </PageHeader>

      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {filtered.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: 'var(--white)', borderRadius: '16px',
                border: '1px solid var(--mist)', padding: '20px 24px',
                display: 'flex', alignItems: 'center',
                gap: '20px', flexWrap: 'wrap',
              }}
            >
              <div style={{
                width: '56px', height: '56px', background: product.gradient,
                borderRadius: '12px', overflow: 'hidden', flexShrink: 0,
              }}>
                {product.image && (
                  <img src={product.image} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: '160px' }}>
                <div style={{
                  fontFamily: 'Syne', fontWeight: 700, fontSize: '0.95rem',
                  color: 'var(--charcoal)', marginBottom: '4px',
                }}>{product.name}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--mid)' }}>
                  {product.mg} · {product.category}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {['Potency', 'Heavy Metals', 'Pesticides', 'Microbials'].map(label => (
                  <span key={label} style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    fontSize: '0.7rem', color: 'var(--accent-green)',
                    background: '#e8f5e9', borderRadius: '6px',
                    padding: '4px 10px', fontWeight: 600,
                  }}>
                    <Check size={10} /> {label}
                  </span>
                ))}
              </div>
              <button style={{
                padding: '10px 20px', borderRadius: '8px',
                background: 'var(--forest)', color: 'var(--white)',
                border: 'none', cursor: 'pointer',
                fontFamily: 'Syne', fontWeight: 600, fontSize: '0.78rem',
                letterSpacing: '0.05em', textTransform: 'uppercase',
                transition: 'background 0.2s', whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-green)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--forest)'}>
                View COA
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const AboutPage = () => {
  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh' }}>
      {/* Hero */}
      <div style={{
        position: 'relative', overflow: 'hidden',
        minHeight: '440px', display: 'flex', alignItems: 'center',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1600&q=80)`,
          backgroundSize: 'cover', backgroundPosition: 'center 30%',
          filter: 'brightness(0.3) saturate(0.7)',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(26,58,42,0.9) 0%, rgba(26,58,42,0.55) 100%)',
        }} />
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', zIndex: 2, position: 'relative', padding: '80px 32px' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p style={{
              fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '16px',
            }}>Our Story</p>
            <h1 style={{
              fontFamily: 'Syne', fontWeight: 700,
              fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
              color: 'var(--white)', letterSpacing: '-0.01em', lineHeight: '1.05',
              marginBottom: '20px',
            }}>
              Curated for the<br />
              <span style={{ color: 'var(--gold)' }}>Discerning Adult.</span>
            </h1>
            <p style={{
              color: 'rgba(255,255,255,0.6)', fontSize: '1.05rem', lineHeight: '1.8',
              maxWidth: '560px', margin: '0 auto',
            }}>
              Mind Blown exists because we were tired of the sketchy gas station shelf.
              We built the hemp experience we always wanted.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Values */}
      <section style={{ padding: '80px 32px', background: 'var(--white)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '40px',
          }} className="values-grid">
            {[
              {
                Icon: FlaskConical,
                title: 'Obsessive Testing',
                body: 'Every single batch goes through a 12-point panel at an accredited third-party lab before it ships. Potency, heavy metals, pesticides, microbials — all of it.',
                color: '#c9a84c',
              },
              {
                Icon: Shield,
                title: 'Vetted Partners Only',
                body: 'We work exclusively with CGMP-certified manufacturing facilities. Every partner goes through a rigorous on-site audit before we list a single one of their products.',
                color: '#4a7c59',
              },
              {
                Icon: Leaf,
                title: 'Radical Transparency',
                body: 'COAs are publicly accessible for every batch, forever. Scan the QR on packaging or search our lab results database anytime. Your trust is our business model.',
                color: '#1a3a2a',
              },
            ].map((val, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                style={{
                  padding: '40px 32px',
                  borderRadius: '20px',
                  border: '1px solid var(--mist)',
                  background: 'var(--smoke)',
                }}
              >
                <div style={{
                  width: '50px', height: '50px',
                  background: val.color + '18',
                  borderRadius: '12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: val.color, marginBottom: '20px',
                }}>
                  <val.Icon size={24} />
                </div>
                <h3 style={{
                  fontFamily: 'Syne', fontWeight: 700, fontSize: '1.1rem',
                  color: 'var(--charcoal)', marginBottom: '12px',
                }}>{val.title}</h3>
                <p style={{ fontSize: '0.88rem', color: 'var(--mid)', lineHeight: '1.75' }}>
                  {val.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '0 32px 80px', background: 'var(--white)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontFamily: 'Syne', fontWeight: 700,
            fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
            color: 'var(--forest)', letterSpacing: '-0.01em', marginBottom: '16px',
          }}>Ready to feel it?</h2>
          <p style={{
            color: 'var(--mid)', fontSize: '1rem', marginBottom: '36px',
          }}>Shop the full collection, or build your custom box.</p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/shop" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'var(--forest)', color: 'var(--white)',
              borderRadius: '8px', padding: '16px 32px',
              fontFamily: 'Syne', fontWeight: 700, fontSize: '0.9rem',
              letterSpacing: '0.05em', textTransform: 'uppercase',
              textDecoration: 'none', transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-green)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--forest)'}>
              Shop Now <ArrowRight size={15} />
            </Link>
            <Link to="/build-box" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'transparent', color: 'var(--forest)',
              border: '2px solid var(--forest)',
              borderRadius: '8px', padding: '16px 32px',
              fontFamily: 'Syne', fontWeight: 700, fontSize: '0.9rem',
              letterSpacing: '0.05em', textTransform: 'uppercase',
              textDecoration: 'none',
            }}>
              Build a Box
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 760px) {
          .values-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};